import { CommentOutlined } from "@ant-design/icons";
import { Button, Drawer, Rate, Select, Tag } from "antd";
import React, { useEffect, useMemo, useState } from "react";

export const DATASET_METADATA_ENUMS = {
    INTENT: ["CUSTOMER_360", "PREDICTIVE_ANALYTICS", "GDPR_DELETION", "MACHINE_LEARNING", "REPORTING", "OPERATIONAL", "ARCHIVAL"],
    TRUSTWORTHINESS: ["VERIFIED", "UNVERIFIED", "GOLD_STANDARD", "SYSTEM_GENERATED", "USER_PROVIDED", "EXTERNAL_SOURCE"],
    QUALITY: ["HIGH_FIDELITY", "STANDARD", "REQUIRES_REVIEW", "LOW_QUALITY", "UNCLEAN", "RAW"],
    STATE: ["PRODUCTION", "STAGING", "EXPERIMENTAL", "DEPRECATED", "DRAFT", "ARCHIVED"],
    SOURCE_TYPE: ["ZERO_PARTY_DATA", "FIRST_PARTY_DATA", "SECOND_PARTY_DATA", "THIRD_PARTY_DATA", "SYNTHETIC_DATA"],
} as const;

export interface IDatasetHeaderMetadataState {
    rating: number;
    tags: {
        intent?: string[];
        trustworthiness?: string[];
        quality?: string[];
        state?: string[];
        sourceType?: string[];
    };
}

interface IDatasetHeaderMetadataProps {
    dataSetId?: string;
    dataSetName?: string;
    initialMetadata?: IDatasetHeaderMetadataState;
    commentCount?: number;
    latestComment?: any;
    onSave: (metadata: IDatasetHeaderMetadataState) => void;
    onShowComments?: () => void;
    readOnly?: boolean;
}

type MetadataGroupKey = keyof typeof DATASET_METADATA_ENUMS;

type MetadataGroup = {
    key: MetadataGroupKey;
    label: string;
    description: string;
    color: string;
    values: readonly string[];
};

const METADATA_GROUPS: MetadataGroup[] = [
    {
        key: "INTENT",
        label: "Intent",
        description: "Why this dataset exists",
        color: "blue",
        values: DATASET_METADATA_ENUMS.INTENT,
    },
    {
        key: "TRUSTWORTHINESS",
        label: "Trustworthiness",
        description: "How reliable it is",
        color: "green",
        values: DATASET_METADATA_ENUMS.TRUSTWORTHINESS,
    },
    {
        key: "QUALITY",
        label: "Quality",
        description: "How strong the data is",
        color: "gold",
        values: DATASET_METADATA_ENUMS.QUALITY,
    },
    {
        key: "STATE",
        label: "State",
        description: "Current lifecycle status",
        color: "geekblue",
        values: DATASET_METADATA_ENUMS.STATE,
    },
    {
        key: "SOURCE_TYPE",
        label: "Source Type",
        description: "Origin of the data",
        color: "purple",
        values: DATASET_METADATA_ENUMS.SOURCE_TYPE,
    },
];

const DEFAULT_METADATA: IDatasetHeaderMetadataState = {
    rating: 0,
    tags: {},
};

const normalizeMetadata = (metadata?: IDatasetHeaderMetadataState): IDatasetHeaderMetadataState => {
    if (!metadata) {
        return DEFAULT_METADATA;
    }

    return {
        rating: metadata.rating ?? DEFAULT_METADATA.rating,
        tags: metadata.tags ?? DEFAULT_METADATA.tags,
    };
};

const getTagGroup = (value: string): MetadataGroup | undefined => {
    return METADATA_GROUPS.find((group) => group.values.includes(value as any));
};

const flattenTags = (tagsObj?: IDatasetHeaderMetadataState["tags"]): string[] => {
    if (!tagsObj) return [];
    return [...(tagsObj.intent || []), ...(tagsObj.trustworthiness || []), ...(tagsObj.quality || []), ...(tagsObj.state || []), ...(tagsObj.sourceType || [])];
};

const unflattenTags = (tagsArray: string[]): IDatasetHeaderMetadataState["tags"] => {
    const result: IDatasetHeaderMetadataState["tags"] = {
        intent: [],
        trustworthiness: [],
        quality: [],
        state: [],
        sourceType: [],
    };

    tagsArray.forEach((tag) => {
        const group = getTagGroup(tag);
        if (group) {
            const key =
                group.key === "INTENT"
                    ? "intent"
                    : group.key === "TRUSTWORTHINESS"
                      ? "trustworthiness"
                      : group.key === "QUALITY"
                        ? "quality"
                        : group.key === "STATE"
                          ? "state"
                          : group.key === "SOURCE_TYPE"
                            ? "sourceType"
                            : undefined;
            if (key && result[key]) {
                result[key]!.push(tag);
            }
        }
    });

    // Remove empty arrays to keep payload clean
    Object.keys(result).forEach((key) => {
        if (result[key as keyof typeof result]?.length === 0) {
            delete result[key as keyof typeof result];
        }
    });

    return result;
};

const DatasetHeaderMetadata: React.FC<IDatasetHeaderMetadataProps> = ({
    dataSetId,
    dataSetName,
    initialMetadata,
    commentCount = 0,
    latestComment,
    onSave,
    onShowComments,
    readOnly = false,
}) => {
    const initial = normalizeMetadata(initialMetadata);
    const [rating, setRating] = useState(initial.rating);
    const [tags, setTags] = useState<string[]>(flattenTags(initial.tags));
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    useEffect(() => {
        const normalized = normalizeMetadata(initialMetadata);
        setRating(normalized.rating);
        setTags(flattenTags(normalized.tags));
    }, [initialMetadata]);

    const groupedOptions = useMemo(
        () =>
            METADATA_GROUPS.map((group) => ({
                label: (
                    <div className="flex items-center justify-between gap-3 py-0.5">
                        <span className="font-semibold text-slate-700">{group.label}</span>
                        <span className="text-[11px] text-slate-500">{group.description}</span>
                    </div>
                ),
                options: group.values.map((value) => ({
                    label: value,
                    value,
                    groupKey: group.key,
                })),
            })),
        [],
    );

    const tagChips = useMemo(
        () =>
            tags.map((tag) => {
                const group = getTagGroup(tag);
                return {
                    value: tag,
                    label: group?.label ?? "Tag",
                    color: group?.color ?? "default",
                };
            }),
        [tags],
    );

    const handleSave = () => {
        onSave({ rating, tags: unflattenTags(tags) });
    };

    const renderTag = (tagProps: any) => {
        const group = getTagGroup(tagProps.value);

        return (
            <Tag color={group?.color ?? "default"} closable={!readOnly} onClose={tagProps.onClose} style={{ marginInlineEnd: 4, marginBottom: 4 }}>
                {tagProps.label}
            </Tag>
        );
    };

    return (
        <>
            {/* Summary Card - Horizontal Compact */}
            <div
                role="button"
                tabIndex={0}
                onClick={() => setIsDrawerOpen(true)}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setIsDrawerOpen(true);
                    }
                }}
                className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-left transition-all hover:border-gray-300 hover:shadow-md"
            >
                <div className="flex items-center gap-4">
                    {/* Rating */}
                    <div className="flex shrink-0 items-center gap-1.5">
                        <Rate value={rating} disabled size="middle" />
                        <span className="text-sm font-semibold text-gray-700">{rating}/5</span>
                    </div>

                    {/* Divider */}
                    <div className="h-4 w-px shrink-0 bg-gray-200" />

                    {/* Tags - scrollable if overflow */}
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
                        {tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {tags.map((tag) => {
                                    const chip = tagChips.find((c) => c.value === tag);
                                    return (
                                        <Tag key={tag} color={chip?.color} className="m-0 text-xs font-medium">
                                            {tag}
                                        </Tag>
                                    );
                                })}
                            </div>
                        ) : (
                            <span className="truncate text-xs font-semibold text-rose-500">Please add metadata tags for better analysis</span>
                        )}
                    </div>

                    {/* Divider */}
                    {(commentCount > 0 || onShowComments) && <div className="h-4 w-px shrink-0 bg-gray-200" />}

                    {/* Comments */}
                    {onShowComments && (
                        <div className="flex shrink-0 items-center">
                            <Button
                                type="dashed"
                                size="middle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onShowComments();
                                }}
                                icon={<CommentOutlined />}
                                className="px-1 text-sm text-gray-500 hover:text-gray-700"
                            >
                                {commentCount > 0 ? ` ${commentCount} comment${commentCount === 1 ? "" : "s"}` : `Add comment`}
                            </Button>
                        </div>
                    )}

                    {/* Arrow hint */}
                    <svg className="h-3.5 w-3.5 shrink-0 text-gray-400" viewBox="0 0 16 16" fill="none">
                        <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>

            {/* Drawer — unchanged */}
            <Drawer title={null} placement="right" width={520} onClose={() => setIsDrawerOpen(false)} open={isDrawerOpen} closeIcon={null} footer={null} bodyStyle={{ padding: 0 }}>
                <div className="flex h-full flex-col">
                    {/* Header */}
                    <div className="border-b border-gray-200 px-6 py-5">
                        <h2 className="text-base font-semibold text-gray-900">Edit Metadata</h2>
                        <p className="mt-0.5 text-sm text-gray-500">Manage quality ratings and dataset tags</p>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-6">
                        <div className="space-y-7">
                            {/* Quality Rating */}
                            <div>
                                <label className="text-sm font-semibold text-gray-900">Quality Rating</label>
                                <p className="mb-3 mt-0.5 text-xs text-gray-500">How well does this dataset meet your quality standards?</p>
                                <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-3">
                                    <Rate value={rating} onChange={setRating} disabled={readOnly} style={{ fontSize: 18 }} />
                                    <span className="text-sm font-bold text-gray-900">{rating}/5</span>
                                    <span className="ml-auto text-sm text-gray-500">
                                        {rating <= 2 ? "Requires review" : rating === 3 ? "Standard quality" : "Production-ready"}
                                    </span>
                                </div>
                            </div>

                            {/* Metadata Tags */}
                            <div>
                                <label className="text-sm font-semibold text-gray-900">Metadata Tags</label>
                                <p className="mb-3 mt-0.5 text-xs text-gray-500">Classify by intent, trustworthiness, quality, and state</p>
                                <Select
                                    mode="multiple"
                                    showSearch
                                    allowClear
                                    placeholder="Select tags..."
                                    className="w-full"
                                    value={tags}
                                    options={groupedOptions}
                                    optionFilterProp="label"
                                    onChange={(values) => setTags(values)}
                                    disabled={readOnly}
                                    maxTagCount="responsive"
                                    tagRender={renderTag}
                                    dropdownMatchSelectWidth={false}
                                    size="large"
                                />
                                {tags.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {tagChips.map((chip) => (
                                            <Tag key={chip.value} color={chip.color} className="m-0 text-xs">
                                                {chip.value}
                                            </Tag>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Comments */}
                            <div>
                                <label className="text-sm font-semibold text-gray-900">
                                    {" "}
                                    <CommentOutlined /> Comments
                                </label>
                                <p className="mb-3 mt-0.5 text-xs text-gray-500">Dataset-level feedback and discussions</p>
                                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                                    <p className="text-sm text-gray-700">
                                        {commentCount > 0 ? `${commentCount} comment${commentCount === 1 ? "" : "s"} attached` : "No comments yet"}
                                    </p>
                                    {onShowComments && (
                                        <Button type="link" onClick={onShowComments} className="h-auto p-0 text-sm">
                                            <CommentOutlined /> View →
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4">
                        <Button onClick={() => setIsDrawerOpen(false)}>Cancel</Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                handleSave();
                                setIsDrawerOpen(false);
                            }}
                            disabled={readOnly}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default DatasetHeaderMetadata;
